import React from "react";
import StarFilled from "../assets/icons/starFilled.svg";
import StarEmpty from "../assets/icons/StarEmpty.svg";
import { endOfWeek, startOfDay, startOfWeek } from "date-fns";
import { Fragment } from "react";
import {
  CURRENCIES,
  SYMBOLS,
  DEFAULT_VALUES,
  ENUMS,
  KEY_CODES,
  USER_ROLES,
  ENTITY_TYPE,
  OFFER_TYPES,
  FIELD_NAME_MAP,
} from "../constants";
import APP_STRINGS from "../languages/EN.json";
import { CircularProgress } from "@material-ui/core";
import ErrorBoundary from "../pages/components/ErrorBoundary/ErrorBoundary";
import jsPDF from "jspdf";
import axios from "axios";

export const webApiCheck = (response) => {
  return (
    response &&
    (response.status === 200 ||
      response.status === 201 ||
      response.status === 204)
  );
};

export const objectHasKeysCheck = (obj) => {
  return obj && obj.constructor === Object && Object.keys(obj).length > 0;
};

// address string of x length
export const getAddressString = (address, length) => {
  return `${address.substring(0, length).toString()}...`;
};

export const getDateAndTime = (epoc) => {
  if (!epoc) return;
  const date = new Date(epoc).toString().split(" ");

  const time = date[4].split(":");
  let format = "AM";

  if (parseInt(time[0]) >= 12) {
    format = "PM";
    if (parseInt(time[0]) !== 12) {
      time[0] = time[0] - 12;
    }
  }

  return `${date[1]} ${date[2]} ${date[3]} (${time[0]}:${time[1]} ${format})`;
};

export const getDatewithddmmyyyy = (epoc) => {
  if (!epoc) return;
  const date = new Date(epoc).toString().split(" ");

  const time = date[4].split(":");
  let format = "AM";

  if (parseInt(time[0]) >= 12) {
    format = "PM";
    if (parseInt(time[0]) !== 12) {
      time[0] = time[0] - 12;
    }
  }

  return ` ${date[2]} ${date[1]} ${date[3]}`;
};

export const getDateAndTimeWithSec = (epoc) => {
  if (!epoc) return;
  const date = new Date(epoc).toString().split(" ");

  const time = date[4].split(":");

  let format = "AM";

  if (parseInt(time[0]) >= 12) {
    format = "PM";
    if (parseInt(time[0]) !== 12) {
      time[0] = time[0] - 12;
    }
  }

  return `${date[1]} ${date[2]} ${date[3]} (${time[0]}:${time[1]}:${time[2]} ${format})`;
};

export const getTimeStamp = (time) => {
  if (!time) return null;

  return new Date(time).getTime();
};

export const capitalizeFirst = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Convert base64 to Blob
export const base64ToBlob = (base64) => {
  const [meta, content] = base64.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const byteString = atob(content);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: mime });
};

export const imageCompressor = async (
  file,
  targetSizeKB = 50,
  maxDimension = 500,
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Resize proportionally if needed
        const scale = Math.min(
          maxDimension / img.width,
          maxDimension / img.height,
          1,
        );
        const width = img.width * scale;
        const height = img.height * scale;
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        const getBase64 = (q) => canvas.toDataURL("image/jpeg", q);
        const base64SizeKB = (base64) =>
          Math.ceil((base64.length * 3) / 4 / 1024); // rough

        let quality = 1.0;
        let base64 = getBase64(quality);
        let size = base64SizeKB(base64);

        while (size > targetSizeKB && quality > 0.01) {
          quality *= 0.5;
          base64 = getBase64(quality);
          size = base64SizeKB(base64);
        }

        if (size > targetSizeKB) {
          console.warn(
            `Image is ${Math.round(size)}KB after compression (target was ${targetSizeKB}KB)`,
          );
        }

        resolve(base64);
      };

      img.onerror = () => reject(new Error("Image load error."));
      img.src = event.target.result;
    };

    reader.onerror = () => reject(new Error("File read error."));
    reader.readAsDataURL(file);
  });
};

// Convert File to base64
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export function convertImageToBase64(url, callback, outputFormat) {
  var canvas = document.createElement("CANVAS");
  var ctx = canvas.getContext("2d");
  var img = new Image();
  img.crossOrigin = "Anonymous";
  img.onload = function () {
    canvas.height = img.height;
    canvas.width = img.width;
    ctx.drawImage(img, 0, 0);
    var dataURL = canvas.toDataURL(outputFormat || "image/png");
    callback.call(this, dataURL);
    canvas = null;
  };
  img.src = url;
}

export const getImageSizeFromBase64 = (base64) => {
  const type = getImageType(base64);
  let stringLength = base64.length - `data:image/${type};base64,`.length;
  let sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.5624896334383812;
  let sizeInMb = sizeInBytes / (1024 * 1024);
  return sizeInMb;
};

export const getImageType = (base64) => {
  const split = base64.split("/");
  const type = split[1].split(";")[0];
  return type;
};

export const getVideoSizeFromBase64 = (base64) => {
  const type = getVideoType(base64);
  let stringLength = base64.length - `data:video/${type};base64,`.length;
  let sizeInBytes = 4 * Math.ceil(stringLength / 3);
  let sizeInMb = sizeInBytes / (1024 * 1024);
  return sizeInMb;
};

export const getVideoType = (base64) => {
  const split = base64.split("/");
  const type = split[1].split(";")[0];
  return type;
};

export const getStatusColor = (id) => {
  switch (id) {
    case 1:
      return "#4CB04E";
    case 2:
      return "#FF2E43";
    case 3:
      return "#FFC41E";
    case 4:
      return "#FC8B55";
    case 5:
      return "#1B66D7";
    case 6:
      return "#7E7E7E";
    default:
      return "inherit";
  }
};

//to check the precentage color of quantity in the vending machine box
export const getPercentageColor = (quantity, total) => {
  let perc = (parseInt(quantity) / parseInt(total)) * 100;
  if (perc < 41) {
    return "#f8e5e5"; // background-color: red;
  } else if (perc && perc > 40 && perc < 71) {
    return "#F8F7E5"; // background-color:yellow
  } else {
    return "#E5F8EC"; //background-color:green
  }
};

//to check the precentage color of quantity in the vending machine box
export const getLiveStockColor = (quantity, total) => {
  let perc = (parseInt(quantity) / parseInt(total)) * 100;
  if (perc < 41) {
    return "red"; // background-color: red;
  } else if (perc && perc > 40 && perc < 71) {
    return "#E2B93B"; // background-color:yellow
  } else {
    return "green"; //background-color:green
  }
};

//to check the precentage color of quantity in the progress bar in thevending machine box
export const getProgressBarColor = (quantity, total) => {
  let perc = (parseInt(quantity) / parseInt(total)) * 100;
  if (perc < 41) {
    return "progress-red";
  } else if (perc && perc > 40 && perc < 71) {
    return "progress-yellow";
  } else {
    return "progress-green";
  }
};

//total quantity in vmsnapshot
export const getTotalQuantityVMSnapshot = (layout_limits) => {
  let totalQty = 0;
  let maxQty = 0;

  layout_limits.forEach((items) => {
    items.forEach((item) => {
      maxQty += item.item_layout_max ? parseInt(item.item_layout_max) : 0;
      totalQty += item.quantity ? item.quantity : 0;
    });
  });
  return { maxQty, totalQty, calString: `${totalQty}/${maxQty}` };
};

export const generateAlphabetArray = (charA, charZ) => {
  let a = [],
    i = charA.charCodeAt(0),
    j = charZ.charCodeAt(0);
  for (; i <= j; ++i) {
    a.push(String.fromCharCode(i));
  }
  // console.log("array:",a)
  return a;
};

export const ALPHABET_ARR = generateAlphabetArray("A", "Z");

//  For Star rating, totalStars is use for how many stars to be shown
export const formRating = (rating, totalStars = DEFAULT_VALUES.STAR_RATING) => {
  let arr = [];
  // for loop is use for dynamic count of star
  for (let i = 1; i <= totalStars; i++) {
    arr.push(i);
  }
  return (
    <Fragment>
      {arr.map((ele) => (
        <img
          src={ele <= rating ? StarFilled : StarEmpty}
          alt="star"
          className="vm-rating"
        />
      ))}
    </Fragment>
  );
};

export const getLabel = (row_number, col_number, type) => {
  switch (type) {
    case "alpha-alpha":
      return `${ALPHABET_ARR[row_number]}-${ALPHABET_ARR[col_number]}`;
    case "alpha-num":
      return `${ALPHABET_ARR[row_number]}-${col_number + 1}`;
    case "num-num":
      return `${row_number + 1}-${col_number + 1}`;
    case "num-alpha":
      return `${row_number + 1}-${ALPHABET_ARR[col_number]}`;
    case "zero-num":
      return `${row_number}-${col_number + 1}`;
    case "one-num":
      return `${row_number + 1}-${col_number}`;
    case "hundred-ten-num":
      return `1${row_number + 1}-${col_number}`;
    default:
      return;
  }
};

export const getRandomColor = () => {
  var letters = "0123456789ABCDEF";
  var color = "#";
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const checkExpiryDate = (startDate, endDate, type) => {
  let msg = "";
  startDate = new Date(startDate).getTime();
  endDate = new Date(endDate).getTime();
  let presentEpoc = new Date().getTime();
  if (presentEpoc > startDate) {
    let timeDiff = endDate - presentEpoc;
    switch (true) {
      case timeDiff <= 1000:
        msg = `The ${type} has expired`;
        break;
      case timeDiff > 1000 && timeDiff <= 60000:
        msg = `The ${type} will expire in ${parseInt(
          timeDiff / 1000,
        )} seconds.`;
        break;
      case timeDiff > 60000 && timeDiff <= 60 * 60000:
        msg = `The ${type} will expire ${parseInt(timeDiff / 60000)} minutes.`;
        break;
      case timeDiff > 60 * 60000 && timeDiff <= 24 * 60 * 60000:
        msg = `The ${type} will expire ${timeDiff / (60 * 60000)} hours.`;
        break;
      case timeDiff > 24 * 60 * 60000 && timeDiff <= 4 * 24 * 60 * 60000:
        msg = `The ${type} will expire ${parseInt(
          timeDiff / (24 * 60 * 60000),
        )} days.`;
        break;
      default:
        msg = "";
        break;
    }
  }
  return msg;
};

//Delete extra fields from json
export const jsonCleanup = (obj, keys, keysAreExceptions = true) => {
  Object.keys(obj).forEach((key) => {
    if (
      keysAreExceptions ? keys.indexOf(key) === -1 : keys.indexOf(key) !== -1
    ) {
      delete obj[key];
    }
  });
  return obj;
};

//Checks if user has permission for something.
export const checkUserHasPermission = (role, permissions) => {
  let flag = false;
  if (role) {
    role.permissions.forEach((per) => {
      if (permissions.indexOf(per) > -1) {
        flag = true;
      }
    });
  }
  return flag;
};

// check motor is activate or not
export const checkMotorActivated = (motors) => {
  let flag = false;
  motors.forEach((motor) => {
    if (motor == 1) {
      flag = true;
    }
  });
  return flag;
};

//Roles list according to user role and permissions. Only for add user screen.
export const getAllowedRolesList = (
  roleCode,
  list,
  userHasPermissionToAddFranchise,
) => {
  switch (roleCode) {
    case USER_ROLES.COMPANY_ADMIN:
      return list.filter((ele) => ele.role_code !== USER_ROLES.COMPANY_PARTNER);
    case USER_ROLES.FRANCHISE_ADMIN:
      return list.filter(
        (ele) =>
          ele.role_code !== USER_ROLES.COMPANY_ADMIN &&
          ele.role_code !== USER_ROLES.COMPANY_PARTNER,
      );
    case USER_ROLES.RETAILER_ADMIN:
      return list.filter(
        (ele) =>
          ele.role_code === USER_ROLES.OPERATOR ||
          ele.role_code === USER_ROLES.TECHNICIAN,
      );
    default:
      return [];
  }
};

export const handleNumberKeyPress = (e) => {
  var key = !isNaN(e.charCode) ? e.charCode : e.keyCode;
  function keyAllowed() {
    var keys = [
      8, 9, 13, 16, 17, 18, 19, 20, 27, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56,
      57, 91, 92, 93,
    ];
    if (key && keys.indexOf(key) === -1) return false;
    else return true;
  }
  if (!keyAllowed()) e.preventDefault();
};

//Submits a form safely when user hits enter
export const safelySubmitFormOnKeyPress = () => {
  document.addEventListener("keypress", (ev) => {
    const { target } = ev;
    //If user hits enter key or numpad key
    if ([KEY_CODES.ENTER_KEY, KEY_CODES.NUMPAD_ENTER_KEY].includes(ev.code)) {
      if (target.type === "search") {
        ev.preventDefault();
      } else {
        let formButton = document.querySelectorAll("form button[type=submit]");
        const lastIndex = formButton.length - 1;
        //check if enter is being hit inside a form and there is a submit button there.
        if (lastIndex >= 0) {
          ev.preventDefault();
          return formButton[lastIndex].click();
        }
      }
    }
  });
};

export const disableNumberInputScrollChange = function () {
  //Disables scroll and change number facility in number input.
  document.addEventListener("wheel", function (event) {
    if (document.activeElement.type === "number") {
      document.activeElement.blur();
    }
  });
};

//calculate percentage of a number.
export const calculatePercentage = (number, percentage) => {
  percentage = parseInt(percentage);
  number = parseInt(number);
  if (!percentage) {
    return number;
  }

  return parseInt(number * (percentage / 100));
};

//prevent any function default behaviour.
export const preventDefaultBehaviour = (e) => {
  e.preventDefault();
};

//delete fields that do not have data.
export const removeNullFieldsFromJson = (payload, exceptions = []) => {
  Object.keys(payload).forEach((key) => {
    if (!payload[key] && exceptions.indexOf(key) === -1) {
      delete payload[key];
    }
  });
  return payload;
};

//returns an url with query parameters.
export const getFullRouteWithQueryString = (route, queryParams) => {
  //Remove keys that has no value
  removeNullFieldsFromJson(queryParams);
  let queryString = "";

  //Get whole queryString.
  for (const [key, value] of Object.entries(queryParams)) {
    queryString += !queryString ? `?${key}=${value}` : `&${key}=${value}`;
  }

  return route + queryString;
};
//returns an url with query parameters.
export const getRouteWithQueryString = (route, queryParams, filters) => {
  //Remove keys that has no value
  removeNullFieldsFromJson(queryParams);
  let queryString = "";

  //Get whole queryString.
  for (const [key, value] of Object.entries(queryParams)) {
    queryString += !queryString ? `${key}=${value}` : `&${key}=${value}`;
  }

  return { pathname: route, search: queryString, state: filters };
};

//Managing filter Object
export const filterObjectHandler = (obj) => {};

//Checks if company has rights to directly transfer products directly from store to vending machine
export const checkStoreToVMapplicable = (props) =>
  props.me.Role.role_code === USER_ROLES.FRANCHISE_ADMIN &&
  props.me.Franchises[0].Franchise.type ===
    ENUMS.FRANCHISE_TYPE.INDEPENDENT.type;

//Checks if user is not company admin && operator admin
export const checkUserNotCaRa = (props) =>
  props.me.Role.role_code !== USER_ROLES.COMPANY_ADMIN &&
  props.me.Role.role_code !== USER_ROLES.OPERATOR;

//Handle Order Type Based On Source Type & Destination Type
export const checkShippingOrderType = (sourceType, destinationType) => {
  if (objectHasKeysCheck(sourceType) && objectHasKeysCheck(destinationType)) {
    if (sourceType.label === "WAREHOUSE" || sourceType.label === "TECHNICIAN_BAG") {
      switch (destinationType.value_to_show) {
        case "Warehouse":
          return "Material Transfer";
        case "Store":
          return "Shipping Order";
        case "Supplier":
          return "Supplier Return";
        case "Discard to bin":
          return "BIN Discarded";
        case "Technician Bag":
          return "Spare Parts Transfer";
      }
    }
    //source is Store
    else {
      switch (destinationType.value_to_show) {
        case "Warehouse":
          return "Store Return";
        case "Store":
          return "Material Transfer";
        case "Supplier":
          return "Supplier Return";
        case "Discard to bin":
          return "BIN Discarded";
        case "Technician Bag":
          return "Spare Parts Transfer";
      }
    }
  } else {
    return "Order Type";
  }
};

//Handle Order Type Based On Source Type & Destination Type
export const checkShippingOrderTypePayload = (sourceType, destinationType) => {
  if (objectHasKeysCheck(sourceType) && objectHasKeysCheck(destinationType)) {
    if (sourceType.label === "WAREHOUSE" || sourceType.label === "TECHNICIAN_BAG") {
      switch (destinationType.value_to_show) {
        case "Warehouse":
          return "MATERIAL_TRANSFER";
        case "Store":
          return "SHIPPING_ORDER";
        case "Supplier":
          return "SUPPLIER_RETURN";
        case "Discard to bin":
          return "DISCARDED_BIN";
        case "Technician Bag":
          return "SPARE_PARTS_TRANSFER";
      }
    }
    //source is Store
    else {
      switch (destinationType.value_to_show) {
        case "Warehouse":
          return "STORE_RETURN";
        case "Store":
          return "MATERIAL_TRANSFER";
        case "Supplier":
          return "SUPPLIER_RETURN";
        case "Discard to bin":
          return "DISCARDED_BIN";
        case "Technician Bag":
          return "SPARE_PARTS_TRANSFER";
      }
    }
  } else {
    return "ORDER_TYPE";
  }
};

//to create destination payload key
export const createDestinationPayloadKey = (destinationType) => {
  switch (destinationType.value_to_show) {
    case "Warehouse":
      return "destination_warehouse_id";
    case "Store":
      return "destination_store_id";
    case "Supplier":
      return "destination_supplier_id";
    case "Technician Bag":
      return "destination_warehouse_id";
  }
};

//Handle change for all input changes.
export const setCustomInputHandler = function (ev, extraData = {}) {
  let {
    target: { name, value, type },
  } = ev;

  //If type of input is number, return value after number coercion.
  if (type === "number" && value !== "") {
    value = Number(value);
  }

  return this.setState({ ...extraData, [name]: value });
};

export const getJsonInInputEventFormat = function (name, value) {
  return { target: { name, value } };
};

//returns string with highlighted text
export const getHighlightedText = function (
  text,
  highlightTexts,
  className = "highlighted-text__yellow",
) {
  if (typeof highlightTexts === "string") {
    //Prevents * error of regular expressions.
    text = text
      .toLowerCase()
      .replace(
        highlightTexts.toLowerCase(),
        `<span class=${className}>${highlightTexts}</span>`,
      );
  } else if (highlightTexts.length) {
    highlightTexts.forEach((element) => {
      //Case insensitive replacement
      var replacePattern = new RegExp(element, "gi");

      text = text.replace(
        replacePattern,
        `<span class=${className}>${element}</span>`,
      );
    });
  }
  return text;
};

export const renderPriceWithSymbol = function (price) {
  if (price) {
    return `${CURRENCIES.RUPEES} ${price}`;
  } else {
    return DEFAULT_VALUES.DOUBLE_DASH;
  }
};
export const renderPercentageWithSymbol = function (number) {
  return `${number} ${SYMBOLS.PERCENTAGE}`;
};
export const renderNetWeightWithSymbol = function (weight, symbol) {
  return `${weight}${symbol.toLowerCase()}`;
};
//Returns object with a component texts to be shown.
export const getComponentTexts = function (location) {
  const splittedLocations = location.split(".");
  let answer = APP_STRINGS[splittedLocations[0]];

  for (let i = 1; i < splittedLocations.length; i++) {
    answer = answer[splittedLocations[i]];
  }

  return answer;
};

//Retreive user current coordinates.
export const retreiveUserLocationCoordinates = async function () {
  return await new Promise((resolve, reject) => {
    const errorMessages = getComponentTexts(
      "common_methods.retreive_user_location_coordinates",
    );

    //If user has accepted location permission.
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          reject(errorMessages.error_unable_to_get_location);
        },
        {
          enableHighAccuracy: true,
        },
      );
    } else {
      reject(errorMessages.error_permission_denied);
    }
  });
};

export const redirectWithPreSelectedFilters = function (data) {};

//Check user and company types starts from here.
export const userIsFranchiseAdmin = function (
  role = this.props.me.Role.role_code,
) {
  return role === USER_ROLES.FRANCHISE_ADMIN;
};

//Check user and company types starts from here.
export const userIsRetailerAdmin = function (
  role = this.props.me.Role.role_code,
) {
  return role === USER_ROLES.RETAILER_ADMIN;
};

export const userIsCompanyAdmin = function (
  role = this.props.me.Role.role_code,
) {
  return role === USER_ROLES.COMPANY_ADMIN;
};

export const userIsTechnicianManager = function (
  role = this.props.me.Role.role_code
) {
  return role === USER_ROLES.ROLE_TECHNICIAN_MANAGER;
};

export const userIsFranchiseAdminFunction = function (me) {
  const role = me?.Role?.role_code; // Optional chaining to avoid undefined errors
  return role === USER_ROLES.FRANCHISE_ADMIN;
};

export const userIsRetailerAdminFunction = function (me) {
  const role = me?.Role?.role_code;
  return role === USER_ROLES.RETAILER_ADMIN;
};

export const userIsCompanyAdminFunction = function (me) {
  const role = me?.Role?.role_code;
  return role === USER_ROLES.COMPANY_ADMIN;
};

export const userIsOperatorAdmin = function (
  role = this.props.me.Role.role_code,
) {
  return role === USER_ROLES.OPERATOR;
};

export const userIsPrimaryCompanyAdmin = function () {
  return (
    userIsCompanyAdmin.call(this) && this.props.me.is_primary_company_admin
  );
};

export const userIsProductPartner = function (
  role = this.props.me.Role.role_code,
) {
  return role === USER_ROLES.COMPANY_PARTNER;
};

export const companyIsWithFranchise = function (
  companyType = this.props.me.Company.type,
) {
  return companyType === ENUMS.COMPANY_TYPE.WITH_FRANCHISE;
};

export const companyIsWithoutFranchise = function (
  companyType = this.props.me.Company.type,
) {
  return companyType === ENUMS.COMPANY_TYPE.WITHOUT_FRANCHISE;
};

export const franchiseIsDependent = function (
  franchiseType = this.props.me.Franchises[0].Franchise.type,
) {
  return franchiseType === ENUMS.FRANCHISE_TYPE.DEPENDENT.type;
};

export const franchiseIsInDependent = function (
  franchiseType = this.props.me.Franchises[0].Franchise.type,
) {
  return franchiseType === ENUMS.FRANCHISE_TYPE.INDEPENDENT.type;
};

//Checks If user is a franchise admin and franchise type is dependent.
export const userIsDependentFranchiseAdmin = function (user) {
  return (
    userIsFranchiseAdmin(user.Role.role_code) &&
    user.Franchises[0].Franchise.type === ENUMS.FRANCHISE_TYPE.DEPENDENT.type
  );
};

//Check if all conditions are satisfied of the functions.
export const checkIfAllConditionsSatisfy = function (...functions) {
  return functions.every((conditionalFunction) => conditionalFunction());
};

// Retreive refund status name
export const getRefundStatusText = function (refundStatus) {
  switch (refundStatus) {
    case 0:
      return "Refund in progress";
    case 1:
      return "Refund Success";
    default:
      return "Not Generated";
  }
};

// get payment status text
export const getPaymentStatusText = function (paymentStatus) {
  switch (paymentStatus) {
    case 0:
      return "Failed";
    case 1:
      return "Success";
    case 3:
      return "Cancelled";
    default:
      return "Processing";
  }
};

// get payment status color
export const getPaymentStatusColor = function (paymentStatus) {
  switch (paymentStatus) {
    case 0:
      return "#DE0000";
    case 1:
      return "#28AE60";
    case 3:
      return "#000000";
    default:
      return "#E4C152";
  }
};

//Retreive purchase order vend status name.
export const getVendingMachineStatusText = function (vendStatus) {
  const vendStatusMsg =
    getComponentTexts("common_methods").get_vending_machine_status_text;
  switch (vendStatus) {
    case 1:
      return vendStatusMsg.vend_success;
    case 2:
      return vendStatusMsg.partial_vend;
    case 3:
      return vendStatusMsg.vend_failure;
    case 4:
      return vendStatusMsg.timed_out;
    case 5:
      return vendStatusMsg.cancelled;
    case 6:
      return vendStatusMsg.auto_cancelled;
    case 7:
      return vendStatusMsg.delayed_payment;
    case 8:
      return vendStatusMsg.partial_payment;
    default:
      return vendStatusMsg.order_processing;
  }
};

//Retreive purchase order vend status color.
export const getVendingMachineStatusTextColor = function (vendStatus) {
  switch (vendStatus) {
    case 1:
      return "#3b953d";
    case 2:
      return "#ffc41e";
    case 3:
      return "#fc8b55";
    case 4:
      return "#1b66d7";
    case 5:
      return "#ff0000";
    default:
      return "#000";
  }
};

//get expiry time text color in refill voerview page.
export const getRefillExpiryTimeTextColor = function (vmExpiry, refillExpiry) {
  if (vmExpiry === null && refillExpiry === null) {
    return "";
  } else if (vmExpiry !== null && refillExpiry !== null) {
    if (
      parseInt(new Date(vmExpiry).getTime()) !==
      parseInt(new Date(refillExpiry).getTime())
    ) {
      return "blue";
    } else {
      return "black";
    }
  } else if (vmExpiry === null && refillExpiry !== null) {
    return "green";
  } else {
    return "red";
  }
};

//Retreive refund status color.
export const getRefundStatusTextColor = function (vendStatus) {
  switch (vendStatus) {
    case 1:
      return "#3b953d";
    case 2:
      return "#ffc41e";
    case 3:
      return "#fc8b55";
    case 4:
      return "#1b66d7";
    case 5:
      return "#ff0000";
    default:
      return "#000";
  }
};

export const concatenateAddressComponents = (ele) => {
  return `${ele.address_line_1} ${
    ele.address_line_2 ? ele.address_line_2 : ""
  } ${ele.State.name} ${ele.City.name} ${ele.pincode}`;
};

export const concatenateCompleteAddress = (ele) => {
  return `${ele.address_line_1 ? ele.address_line_1 : ""} ${
    ele.address_line_2 ? ele.address_line_2 : ""
  }${ele.Country ? ele.Country.name : ""} ${ele.State ? ele.State.name : ""} ${
    ele.City ? ele.City.name : ""
  } ${ele.pincode ? ele.pincode : ""}`;
};

// function to check user window size
export const checkMaxWindowSize = function (maxWindowSize) {
  return window.matchMedia(`(max-width:${maxWindowSize}px)`).matches;
};

//Returns an unique custom input name which can be used for dynamic validation.
//Example: min_quantity_12,min_quantity_23
export const getUniqueDynamicInputName = function (element, suffixKey) {
  return function (prefix) {
    return `${prefix}_${element[suffixKey]}`;
  };
};

//Do not forget to bind this to this function.
export const enableFormDirtiness = function (callback) {
  if (!this.props.formDirtBool) {
    this.props.setFormDirtiness();
    this.btns[1].btnProps.method = () =>
      this.props.checkShowModal(
        callback ? callback : () => this.props.history.goBack(),
      );
  }
};

//Returns current page number by offset.
export const calculatePageNumberFromOffset = function (params) {
  const { offset, limit } = params;
  try {
    return offset / limit + 1;
  } catch {
    return 1;
  }
};

export const stringToNumberArrayConversion = function (str, separator = ",") {
  return str.length === 0
    ? []
    : str.split(separator).map((ele) => parseInt(ele));
};

export const ellipsisString = function (value, wordsLimit) {
  return value.length > 8 ? value.substring(0, wordsLimit) + "..." : value;
};

export function RenderLazyComponent(Component) {
  return function (props) {
    return (
      <React.Suspense
        fallback={
          <>
            <CircularProgress
              classes={{
                root: "circular-loader",
                colorPrimary: "circular-loader-color",
              }}
            />
          </>
        }
      >
        <ErrorBoundary>
          <Component {...props} />
        </ErrorBoundary>
      </React.Suspense>
    );
  };
}

export const downloadImageAsPdf = function (imgUrl) {
  const doc = new jsPDF("p", "mm", [297, 210]);
  doc.addImage(imgUrl, "png", 45, 30, 120, 120);
  doc.save(`${new Date().getTime()}`);
};

export const downloadImageHandler = function (imgUrl) {
  let split = imgUrl.split(".");
  fetch(imgUrl, {
    method: "GET",
    headers: {},
  })
    .then((response) => {
      response.arrayBuffer().then(function (buffer) {
        const url = window.URL.createObjectURL(new Blob([buffer]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${new Date().getTime()}.${split[split.length - 1]}`,
        ); //or any other extension
        document.body.appendChild(link);
        link.click();
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

export function hasElementInViewPort(domElement) {
  if (!domElement) {
    return false;
  }
  //Returns element geometry in respect to screen.
  const elementGeometry = domElement.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;

  return (
    elementGeometry.left >= 0 &&
    elementGeometry.top >= 0 &&
    elementGeometry.right <= innerWidth &&
    elementGeometry.bottom <= innerHeight
  );
}

// //get unique SKU Refill Plan Product
// export const getUniqueSkuData = (RefillDetails) => {
//   //Sum up all the same taxid tax_value
//   var temp = {};
//   RefillDetails.forEach(function (obj) {
//     if (obj.Product) {
//       if (!temp[obj.Product.sku]) {
//         temp[obj.Product.sku] = { ...obj };
//       } else {
//         temp[obj.Product.sku.refilled_items] =
//           Number(temp[obj.Product.sku.refilled_items]) +
//           Number(obj.refilled_items ? obj.refilled_items : 0);
//         temp[obj.Product.sku.removed_items] =
//           Number(temp[obj.Product.sku.refilled_items]) +
//           Number(obj.removed_items ? obj.removed_items : 0);
//       }
//     }
//   });
//   console.log("temp", temp);
//   //Format the data into desired output
//   var result = [];
//   for (var key in temp) {
//     result.push({
//       ...temp[key],
//     });
//   }

//   console.log("result", result);
//   return result;
// };

export function FilterWarehouseStore(
  array,
  filterWarehouse = [],
  filterStore = [],
) {
  let arrayWarehouse = [];
  let arrayStore = [];
  if (filterWarehouse.length > 0) {
    arrayWarehouse = array.filter((item) => {
      return (
        filterWarehouse.indexOf(item.Warehouse ? item.Warehouse.name : null) >
        -1
      );
    });
  }

  if (filterStore.length > 0) {
    arrayStore = array.filter((item) => {
      return filterStore.indexOf(item.Store ? item.Store.name : null) > -1;
    });
  }
  let resultArray = [...arrayWarehouse, ...arrayStore];
  return resultArray;
}

//to check whether tray is featured or not
export const checkFeatureTray = (featureRows, row) => {
  if (featureRows.length) {
    return featureRows.includes(row);
  } else {
    return false;
  }
};

//check whether cell motor is activated or not
export const checkCellMotorActivated = (cell) => {
  return cell.motor_activated.some((element) => element === 1);
};

//check if value is number
export function isNumber(value) {
  return typeof value === "number";
}

// Create a function to convert the data to CSV format

export const convertToCSV = (data) => {
  const csv = data
    .map((row) => {
      // Iterate through each value in the row
      const rowValues = row.map((value) => {
        // Check if the value contains a comma, and if so, enclose it in double quotes

        if (typeof value === "string" && value.includes(",")) {
          return `"${value}"`;
        }
        return value;
      });
      return rowValues.join(",");
    })
    .join("\n");
  return csv;
};

//gives today date with start time
export function getStartDay() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setHours(0, 0, 0, 0); // Set time to 00:00:00:000 (midnight)
  return currentDate; // Get the start of the current day in milliseconds
}

//gives this week date i.e.,this week's Sunday date
export function getThisWeekDay() {
  const today = startOfDay(new Date()); // Get the current date and time
  const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 0 });
  startOfCurrentWeek.setHours(0, 0, 0, 0); // Set time to 00:00:00:000 (midnight)
  return startOfCurrentWeek; // Get the start of the current day in milliseconds
}

//gives this week last date i.e.,this week's saturday date
export function getThisWeekEndDay() {
  const today = new Date(); // Get the current date and time
  const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 0 }); // Sunday as the start of the week
  endOfCurrentWeek.setHours(23, 59, 59, 999); // Set time to 23:59:59:999 (end of the day)
  return endOfCurrentWeek; // Get the end of the current day in milliseconds
}

//gives today date with end time
export function getEndDay() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setHours(23, 59, 59, 999); // Set time to 23:59:59:999 (end of the day)
  return currentDate; // Get the end of the current day in milliseconds
}

//gives start time fo today in milliseconds
export function getStartOfDay() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setHours(0, 0, 0, 0); // Set time to 00:00:00:000 (midnight)
  return currentDate.getTime(); // Get the start of the current day in milliseconds
}

//gives start time fo today in milliseconds
export function getEndOfDay() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setHours(23, 59, 59, 999); // Set time to 23:59:59:999 (end of the day)
  return currentDate.getTime(); // Get the end of the current day in milliseconds
}

//gives start time fo yesterday in milliseconds
export function getStartOfYesterday() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setDate(currentDate.getDate() - 1); // Set the date to yesterday
  currentDate.setHours(0, 0, 0, 0); // Set time to 00:00:00:000 (midnight)
  return currentDate.getTime(); // Get the start of yesterday in milliseconds
}

//gives end time fo yesterday in milliseconds
export function getEndOfYesterday() {
  const currentDate = new Date(); // Get the current date and time
  currentDate.setDate(currentDate.getDate() - 1); // Set the date to yesterday
  currentDate.setHours(23, 59, 59, 999); // Set time to 23:59:59:999 (end of the day)
  return currentDate.getTime(); // Get the end of yesterday in milliseconds
}

// to convert milliseconds into AM/PM format time format
export function formatDateTime(timestamp) {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // Use 12-hour clock format
  };

  const formattedDateTime = new Date(timestamp).toLocaleString(
    undefined,
    options,
  );
  return formattedDateTime;
}

//upload in s3 using temporary url generated from  backend
export const uploadVideoToS3 = async (s3Url, videoFile) => {
  try {
    const response = await axios.put(s3Url, videoFile, {
      headers: {
        "Content-Type": videoFile.type,
      },
    });

    if (response.status === 200) {
      const videoUrl = new URL(response.config.url);
      return videoUrl.origin + videoUrl.pathname;
    } else {
      console.error("Failed to upload video to S3.");
      return null;
    }
  } catch (error) {
    console.error("Error uploading video to S3:", error);
    return null;
  }
};

//Roles list according to user role . Only for vm template screen.
export const getAllowedAdminRolesList = (roleCode) => {
  switch (roleCode) {
    case USER_ROLES.FRANCHISE_ADMIN:
      return ENTITY_TYPE.filter((ele) => ele.id !== 1);
    case USER_ROLES.RETAILER_ADMIN:
      return ENTITY_TYPE.filter((ele) => ele.id === 3);
    default:
      return ENTITY_TYPE;
  }
};

//fucniton to return offer type string based on multiple suboffers for list api
export const formatOfferTypes = (subOffers) => {
  const offerTypeCounts = subOffers.reduce((acc, subOffer) => {
    const offerTypeName = OFFER_TYPES[subOffer.offer_type_id];
    acc[offerTypeName] = (acc[offerTypeName] || 0) + 1;
    return acc;
  }, {});

  const offerTypesArray = Object.keys(offerTypeCounts).map((key) => {
    return offerTypeCounts[key] > 1 ? `${key} ${offerTypeCounts[key]}+` : key;
  });

  if (offerTypesArray.length > 2) {
    return `${offerTypesArray[0]}, ${offerTypesArray.length - 1}+`;
  } else {
    return offerTypesArray.join(", ");
  }
};

// Function to create a Date object with the current date and specified time
export const createTimeWithCurrentDate = (time) => {
  const [hours, minutes, seconds] = time.split(":");
  const date = new Date();
  date.setHours(hours, minutes, seconds);
  return date;
};

// to modify api data for ui
export const getSubOffersData = (subOffer) => {
  const { SubOfferProducts } = subOffer;
  let product = {};
  let product_2 = {};
  let offer_type = subOffer.offer_type_id;
  SubOfferProducts.forEach((productObj) => {
    if (productObj.primary) {
      product = {
        ...productObj.Product,
        primary: true,
      };
    } else {
      product_2 = {
        ...productObj.Product,
        primary: false,
      };
    }
  });

  const [firstProductObj] = SubOfferProducts;

  // Common base sub offer object
  const baseOffer = {
    select_product: product,
    offer_type: subOffer.offer_type_id || null,
    total_stock: firstProductObj?.total_stock || null,
    sub_offer_id: firstProductObj?.sub_offer_id,
    u_id: `${Date.now()}_${firstProductObj.id}`,
    is_active: Number(subOffer.is_active),
    label: subOffer.label,
  };

  switch (offer_type) {
    case 1: // FLAT
      return {
        ...baseOffer,
        offer_amount: firstProductObj?.offer_value
          ? parseFloat(firstProductObj.offer_value)
          : null,
        offer_price:
          parseFloat(product.mrp - firstProductObj.offer_value) || null,
        offer_qty: firstProductObj?.max_buy_qty || null,
      };
    case 2: // PERCENTAGE
      return {
        ...baseOffer,
        off_percent: firstProductObj?.offer_value
          ? parseFloat(firstProductObj.offer_value)
          : null,
        offer_price: parseFloat(
          product?.mrp - (firstProductObj?.offer_value / 100) * product?.mrp,
        ),
        offer_qty: firstProductObj?.max_buy_qty || null,
      };
    case 3: // COMBO
      return {
        ...baseOffer,
        select_product2: product_2,
        offer_price: subOffer.offer_price || null,
        total_stock: subOffer.total_stock || null,
        image: subOffer.image,
      };
    case 4: // LIQUIDATION
      return {
        ...baseOffer,
        off_percent: firstProductObj?.offer_value
          ? parseFloat(firstProductObj.offer_value)
          : null,
        offer_price: parseFloat(
          product?.mrp - (firstProductObj?.offer_value / 100) * product?.mrp,
        ),
      };
    case 5: // BUY X ITEMS GET Y ITEMS
      return {
        ...baseOffer,
        offer_price: firstProductObj?.offer_value || null,
        buy_qty: firstProductObj?.buy_qty,
        free_qty: firstProductObj?.free_qty,
      };
    case 6: // BUY X PRODUCT GET Y PRODUCT FREE
      return {
        ...baseOffer,
        select_product2: product_2,
        offer_price: firstProductObj?.offer_value || null,
      };
    case 7: // BUY X ITEMS FOR Y TOTAL AMOUNT
      return {
        ...baseOffer,
        buy_qty: firstProductObj?.buy_qty,
        offer_price: firstProductObj?.offer_value || null,
      };
    case 8: // SHOP X AMOUNT GET Y ITEM FREE
      return {
        ...baseOffer,
        offer_price: subOffer.offer_price || null,
        cart_amount: subOffer.total_stock || null,
      };
  }
};

//HELPER FUNCTION FOR  MASTER CONFIG  START
const EXCLUDED_FROM_CALCULATION = ["action-history"];

export const getRequiredFieldsForSection = (sectionId, sectionConfigs) => {
  const config = sectionConfigs[sectionId];
  if (!config) return [];

  const requiredFields = [];

  config.formSections.forEach((formSection) => {
    formSection.fields.forEach((field) => {
      if (field.required) {
        requiredFields.push(field.name);
      }
    });
  });

  return requiredFields;
};

export const isFieldFilled = (value, isMultiSelect = false) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (isMultiSelect) {
    return Array.isArray(value) && value.length > 0;
  }

  // For objects (like dropdown selections with {id, name})
  if (typeof value === "object" && !Array.isArray(value)) {
    return value.id !== undefined && value.id !== null;
  }

  return true;
};

export const isSectionComplete = (sectionId, formData, sectionConfigs) => {
  const requiredFields = getRequiredFieldsForSection(sectionId, sectionConfigs);

  // If no required fields, section is complete
  if (requiredFields.length === 0) {
    return true;
  }

  // Check if all required fields are filled
  return requiredFields.every((fieldName) => {
    const config = sectionConfigs[sectionId];
    const field = config.formSections
      .flatMap((fs) => fs.fields)
      .find((f) => f.name === fieldName);

    const isMultiSelect = field?.multi_select || false;
    const value = formData[fieldName];

    return isFieldFilled(value, isMultiSelect);
  });
};

export const getSectionStatus = (sectionId, formData, sectionConfigs) => {
  if (EXCLUDED_FROM_CALCULATION.includes(sectionId)) {
    return "complete";
  }

  return isSectionComplete(sectionId, formData, sectionConfigs)
    ? "complete"
    : "warning";
};

export const getCompletedSectionsCount = (
  sections,
  formData,
  sectionConfigs,
) => {
  return sections.filter((section) => {
    if (EXCLUDED_FROM_CALCULATION.includes(section.id)) {
      return false;
    }
    const status = getSectionStatus(section.id, formData, sectionConfigs);
    return status === "complete";
  }).length;
};

export const getFieldConfig = (sectionId, fieldName, sectionConfigs) => {
  const config = sectionConfigs[sectionId];
  if (!config) return null;

  for (const formSection of config.formSections) {
    const field = formSection.fields.find((f) => f.name === fieldName);
    if (field) return field;
  }

  return null;
};

export const formatValidationErrors = (errors) => {
  const errorMessages = [];
  Object.keys(errors).forEach((field) => {
    const fieldLabel = FIELD_NAME_MAP[field] || field;
    const errorArray = errors[field];
    errorArray.forEach((error) => {
      errorMessages.push(`${fieldLabel}: ${error}`);
    });
  });

  return errorMessages;
};

export const formatDateToDisplay = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export function formatTimestamp(timestamp) {
  if (!timestamp) {
    return "-";
  }

  try {
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) {
      return "-";
    }

    // Format: DD-MMM-YYYY HH:MM:SS
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "-";
  }
}

//HELPER FUNCTION FOR  MASTER CONFIG  END
